/**
 * HostingAccountDetail.tsx
 *
 * Customer-facing detail page for one shared-hosting account. Shows:
 *   - Plan + status
 *   - Primary domain + IP + nameservers
 *   - SFTP credentials (with show/copy)
 *   - MySQL root password (with show/copy)
 *   - Quotas (disk/ram/cpu/bandwidth) and current usage
 *
 * Phase 1 is read-only. Phase 2 adds the file manager / DBs / DNS panels.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HostingFileManager from './HostingFileManager';
import HostingLogsViewer from './HostingLogsViewer';
import MailboxesPanel from './MailboxesPanel';
import PhpVersionPanel from './PhpVersionPanel';
import DnsGuideModal from './DnsGuideModal';
import DashShell from '../dash/DashShell';

interface HostingDetail {
  id: string;
  primaryDomain: string | null;
  hostname: string | null;
  ipAddress: string | null;
  status: string;
  packageName: string | null;
  diskQuotaGb: number | null;
  ramQuotaMb: number | null;
  cpuQuota: number | null;
  bandwidthQuotaGb: number | null;
  currentDiskUsageMb: number | null;
  currentBandwidthUsedMb: number | null;
  sftpUsername: string;
  sftpPassword: string | null;
  sftpHost: string | null;
  sftpPort: number;
  mysqlRootPassword: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  dnsLinkedAt: string | null;
  sslIssuedAt: string | null;
  createdAt: string;
}

const HostingAccountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState<HostingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSftpPw, setShowSftpPw] = useState(false);
  const [showMysqlPw, setShowMysqlPw] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [showDnsGuide, setShowDnsGuide] = useState(false);
  const [dnsVerifyBusy, setDnsVerifyBusy] = useState(false);
  const [dnsVerifyMsg, setDnsVerifyMsg] = useState('');
  const [dnsVerifyOk, setDnsVerifyOk] = useState(false);

  useEffect(() => {
    if (authLoading || !user || !id) return;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        const resp = await fetch(`${apiBase}/api/hosting/accounts/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const json = await resp.json();
        if (json.success) setData(json.data);
        else setError(json.error || 'Failed to load hosting account');
      } catch (e: any) {
        setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [authLoading, user, id]);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* */ }
  };

  const verifyDns = async () => {
    if (!data) return;
    setDnsVerifyBusy(true); setDnsVerifyMsg('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${data.id}/dns/verify`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      setDnsVerifyMsg(json.message || (json.success ? 'Checked.' : 'Verification failed'));
      if (json.success && json.data?.pointed) {
        setDnsVerifyOk(true);
        setData(d => (d ? { ...d, dnsLinkedAt: new Date().toISOString() } : d));
      }
    } catch (e: any) {
      setDnsVerifyMsg(e.message || 'Network error');
    } finally {
      setDnsVerifyBusy(false);
    }
  };


  if (authLoading || loading) {
    return (
      <DashShell title="Hosting account" subtitle="Loading…">
        <div className="h-40 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }
  if (!user) {
    return (
      <DashShell title="Hosting account" subtitle="Sign in required">
        <div className="rounded-2xl bg-white/80 backdrop-blur border border-white/40 p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Please log in</h2>
          <p className="text-slate-600">You need to be signed in to view this account.</p>
        </div>
      </DashShell>
    );
  }
  if (error || !data) {
    return (
      <DashShell title="Hosting account" subtitle="Not loaded">
        <div className="rounded-2xl bg-secondary-100 border border-secondary-500 p-6 text-center max-w-md mx-auto">
          <p className="text-primary-navy mb-3">{error || 'Hosting account not found'}</p>
          <button onClick={() => navigate('/customer/hosting')} className="text-primary-navy hover:underline">← Back to hosting</button>
        </div>
      </DashShell>
    );
  }

  const diskPct = data.diskQuotaGb
    ? Math.round(((data.currentDiskUsageMb || 0) / (data.diskQuotaGb * 1024)) * 100)
    : 0;
  const bwPct = data.bandwidthQuotaGb
    ? Math.round(((data.currentBandwidthUsedMb || 0) / (data.bandwidthQuotaGb * 1024)) * 100)
    : 0;

  const overview = (
    <>
      {/* DNS pointing is ALWAYS a manual act by whoever controls the
          domain — 12and22 never edits hosted zones for hosting accounts,
          by policy. This banner only shows the required records. */}
      {data.primaryDomain && !data.dnsLinkedAt && !data.sslIssuedAt && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="font-medium text-amber-900">Point your domain to go live</p>
              <p className="text-sm text-amber-800 mt-1">
                Add these records for <strong>{data.primaryDomain}</strong> wherever its DNS is managed.
                12and22 never changes DNS for you — pointing your domain is always your action, at your DNS provider.
              </p>
              <div className="mt-2 font-mono text-xs bg-white/70 border border-amber-200 rounded-lg p-3 text-slate-700 space-y-1">
                <div>A&nbsp;&nbsp;&nbsp;@&nbsp;&nbsp;&nbsp;→&nbsp;&nbsp;{data.ipAddress || '(provisioning)'}</div>
                <div>A&nbsp;&nbsp;&nbsp;www&nbsp;→&nbsp;&nbsp;{data.ipAddress || '(provisioning)'}</div>
              </div>
              <p className="text-xs text-amber-800 mt-2">
                Already added the records? Click Verify — once it resolves here, issue SSL from the SSL tab (auto-renews).
                Propagation can take up to 48 hours.
              </p>
              {dnsVerifyMsg && (
                <p className={`text-sm mt-2 ${dnsVerifyOk ? 'text-emerald-700' : 'text-amber-900'}`}>{dnsVerifyMsg}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  onClick={verifyDns}
                  disabled={dnsVerifyBusy}
                  className="text-sm bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-60 font-medium transition"
                >
                  {dnsVerifyBusy ? 'Checking DNS…' : 'Verify DNS'}
                </button>
                <button
                  onClick={() => setShowDnsGuide(true)}
                  className="text-sm font-medium text-amber-900 underline hover:text-amber-700"
                >
                  View the complete record list (MX, SPF, DKIM, DMARC…)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDnsGuide && data.primaryDomain && (
        <DnsGuideModal hostingAccountId={data.id} onClose={() => setShowDnsGuide(false)} />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Connection info */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Connection</h2>
          <Row label="Server IP" value={data.ipAddress} onCopy={() => data.ipAddress && copy(data.ipAddress, 'ip')} copied={copied === 'ip'} mono />
          <Row label="Primary domain" value={data.primaryDomain} />
          <Row label="Hostname" value={data.hostname} mono />
        </div>

        {/* SFTP */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">SFTP / File Access</h2>
          <Row label="Host" value={data.sftpHost} mono />
          <Row label="Port" value={String(data.sftpPort)} mono />
          <Row label="Username" value={data.sftpUsername} onCopy={() => copy(data.sftpUsername, 'sftpu')} copied={copied === 'sftpu'} mono />
          <div className="flex justify-between items-center text-sm py-1.5">
            <span className="text-gray-600">Password</span>
            <div className="flex items-center gap-2">
              <span className="font-mono">
                {showSftpPw ? (data.sftpPassword || '—') : '••••••••'}
              </span>
              <button onClick={() => setShowSftpPw(s => !s)} className="text-blue-600 text-xs hover:underline">
                {showSftpPw ? 'Hide' : 'Show'}
              </button>
              {data.sftpPassword && (
                <button onClick={() => copy(data.sftpPassword!, 'sftpp')} className="text-blue-600 text-xs hover:underline">
                  {copied === 'sftpp' ? '✓' : 'Copy'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* MySQL */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">MySQL (MariaDB)</h2>
          <Row label="Host" value="localhost" mono />
          <Row label="Root user" value="root" mono />
          <div className="flex justify-between items-center text-sm py-1.5">
            <span className="text-gray-600">Root password</span>
            <div className="flex items-center gap-2">
              <span className="font-mono">
                {showMysqlPw ? (data.mysqlRootPassword || '—') : '••••••••'}
              </span>
              <button onClick={() => setShowMysqlPw(s => !s)} className="text-blue-600 text-xs hover:underline">
                {showMysqlPw ? 'Hide' : 'Show'}
              </button>
              {data.mysqlRootPassword && (
                <button onClick={() => copy(data.mysqlRootPassword!, 'mysqlp')} className="text-blue-600 text-xs hover:underline">
                  {copied === 'mysqlp' ? '✓' : 'Copy'}
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            MySQL only accepts connections from inside your container (localhost).
          </p>
        </div>

        {/* Quotas */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Resources</h2>
          <UsageBar
            label="Disk"
            used={`${(data.currentDiskUsageMb || 0)} MB`}
            total={`${data.diskQuotaGb ?? '-'} GB`}
            pct={diskPct}
          />
          <UsageBar
            label="Bandwidth (this period)"
            used={`${(data.currentBandwidthUsedMb || 0)} MB`}
            total={`${data.bandwidthQuotaGb ?? '-'} GB`}
            pct={bwPct}
          />
          <Row label="CPU" value={`${data.cpuQuota ?? '-'} core(s)`} />
          <Row label="RAM" value={`${data.ramQuotaMb ?? '-'} MB`} />
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-900 mb-3">Account</h2>
        <p className="text-xs text-gray-500 mb-3">Account management. Some actions are not yet available — contact support for help.</p>
        <div className="flex flex-wrap gap-2">
          <button disabled className="text-xs bg-gray-100 text-gray-400 cursor-not-allowed px-3 py-1.5 rounded">Upgrade plan (soon)</button>
          <button disabled className="text-xs bg-gray-100 text-gray-400 cursor-not-allowed px-3 py-1.5 rounded">Rotate SFTP password (soon)</button>
          <button disabled className="text-xs bg-red-50 text-red-400 cursor-not-allowed px-3 py-1.5 rounded">Cancel hosting (soon)</button>
        </div>
      </div>
    </>
  );

  // Sidebar sections. Each renders ONE panel in the right-hand content area.
  const sections: Array<{ key: string; label: string; icon: string; render: () => React.ReactNode }> = [
    { key: 'overview',   label: 'Overview',    icon: '🏠', render: () => overview },
    { key: 'files',      label: 'File Manager', icon: '📁', render: () => <HostingFileManager hostingAccountId={data.id} /> },
    { key: 'databases',  label: 'Databases',   icon: '🗄️', render: () => <MySQLDatabasesPanel hostingAccountId={data.id} /> },
    { key: 'apps',       label: 'WordPress',   icon: '🚀', render: () => <InstalledAppsPanel hostingAccountId={data.id} primaryDomain={data.primaryDomain} /> },
    { key: 'subdomains', label: 'Subdomains',  icon: '🔗', render: () => <SubdomainsPanel hostingAccountId={data.id} primaryDomain={data.primaryDomain} /> },
    { key: 'mailboxes',  label: 'Mailboxes',   icon: '✉️', render: () => <MailboxesPanel hostingAccountId={data.id} primaryDomain={data.primaryDomain} /> },
    { key: 'ssl',        label: 'SSL',         icon: '🔒', render: () => <SslPanel hostingAccountId={data.id} /> },
    { key: 'php',        label: 'PHP Version', icon: '🐘', render: () => <PhpVersionPanel hostingAccountId={data.id} /> },
    { key: 'cron',       label: 'Cron Jobs',   icon: '⏰', render: () => <CronJobsPanel hostingAccountId={data.id} /> },
    { key: 'backups',    label: 'Backups',     icon: '💾', render: () => <BackupsPanel hostingAccountId={data.id} /> },
    { key: 'logs',       label: 'Logs',        icon: '📜', render: () => <HostingLogsViewer hostingAccountId={data.id} /> },
  ];
  const active = sections.find(s => s.key === activeSection) || sections[0];

  return (
    <DashShell
      title={data.primaryDomain || data.hostname || data.id}
      subtitle={`${data.packageName || 'Hosting'} · ${data.hostname || ''}`}
      right={<StatusBadge status={data.status} />}
    >
      <div>
        <Link to="/customer/hosting" className="text-primary-navy hover:underline text-sm">← All hosting accounts</Link>

        {data.status === 'suspended' && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
            <div className="font-medium text-amber-900">Account suspended</div>
            {data.suspendedReason && <div className="text-sm text-amber-800 mt-1">{data.suspendedReason}</div>}
          </div>
        )}

        {/* Mobile: dropdown nav */}
        <div className="md:hidden mb-4">
          <select
            value={activeSection}
            onChange={e => setActiveSection(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-medium"
          >
            {sections.map(s => (
              <option key={s.key} value={s.key}>{s.icon} {s.label}</option>
            ))}
          </select>
        </div>

        {/* Desktop: sidebar + main */}
        <div className="md:grid md:grid-cols-12 md:gap-6">
          <aside className="hidden md:block md:col-span-3 lg:col-span-2">
            <nav className="bg-white rounded-lg border border-gray-200 p-2 sticky top-4">
              {sections.map(s => (
                <button
                  key={s.key}
                  onClick={() => setActiveSection(s.key)}
                  className={`w-full text-left px-3 py-2 rounded text-sm font-medium flex items-center gap-2 mb-0.5
                    ${active.key === s.key
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <span className="text-base">{s.icon}</span>
                  <span>{s.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          <main className="md:col-span-9 lg:col-span-10">
            {active.render()}
          </main>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          Created {new Date(data.createdAt).toLocaleString()} · Account ID: <span className="font-mono">{data.id}</span>
        </div>
      </div>
    </DashShell>
  );
};

// ============================================================================
// SSL panel — Let's Encrypt only. Shows DNS-pointing preconditions, cert
// status/expiry, and an explicit issue/renew action. Never touches DNS.
// ============================================================================

interface SslStatus {
  domain: string;
  serverIp: string | null;
  apexResolvedIps: string[];
  wwwResolvedIps: string[];
  pointed: boolean;
  wwwPointed: boolean;
  certInstalled: boolean;
  certExpiresAt: string | null;
  sslIssuedAt: string | null;
  autoRenews: boolean;
}

const SslPanel: React.FC<{ hostingAccountId: string }> = ({ hostingAccountId }) => {
  const [status, setStatus] = useState<SslStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });
  const apiBase = process.env.REACT_APP_API_URL || '';

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/ssl`, { headers: authHeaders() });
      const json = await resp.json();
      if (json.success) setStatus(json.data);
      else setErr(json.error || 'Failed to load SSL status');
    } catch (e: any) { setErr(e.message || 'Network error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const issue = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/ssl/issue`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await resp.json();
      if (json.success) { setMsg(json.message || 'Certificate issued.'); await load(); }
      else setErr(json.error || 'Certificate issuance failed');
    } catch (e: any) { setErr(e.message || 'Network error'); }
    finally { setBusy(false); }
  };

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  if (loading) {
    return <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-500">Checking SSL status…</div>;
  }
  if (!status) {
    return <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-red-600">{err || 'SSL status unavailable'}</div>;
  }

  const expiringSoon = status.certExpiresAt
    && (new Date(status.certExpiresAt).getTime() - Date.now()) < 21 * 24 * 3600 * 1000;

  return (
    <div className="space-y-4">
      {err && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>}
      {msg && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">{msg}</div>}

      {/* Certificate status */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-semibold text-gray-900">Certificate — {status.domain}</h2>
            {status.certInstalled ? (
              <p className="text-sm text-gray-600 mt-1">
                <span className="inline-flex items-center gap-1.5 text-green-700 font-medium">● Active</span>
                {' · '}expires {fmtDate(status.certExpiresAt)}
                {expiringSoon && <span className="text-amber-700"> (renewal due soon — renews automatically)</span>}
              </p>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                <span className="inline-flex items-center gap-1.5 text-amber-700 font-medium">● No certificate yet</span>
                {' — '}your site is served over HTTP only until one is issued.
              </p>
            )}
          </div>
          <button
            onClick={issue}
            disabled={busy || !status.pointed}
            className="text-sm bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 font-medium transition"
          >
            {busy ? 'Running Let’s Encrypt…' : status.certInstalled ? 'Renew / re-check now' : 'Issue free certificate'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Certificates are free via Let’s Encrypt, valid 90 days, and renew automatically on the server — no action needed after the first issue.
        </p>
      </div>

      {/* DNS precondition */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-900 mb-3">DNS check</h2>
        <div className="space-y-2 text-sm">
          <DnsCheckRow name={status.domain} ok={status.pointed} resolved={status.apexResolvedIps} expected={status.serverIp} />
          <DnsCheckRow name={`www.${status.domain}`} ok={status.wwwPointed} resolved={status.wwwResolvedIps} expected={status.serverIp} optional />
        </div>
        {!status.pointed && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
            The certificate can only be issued after <strong>{status.domain}</strong> points to <strong>{status.serverIp}</strong> at
            your DNS provider (12and22 never changes DNS for you). Add the A record, wait for propagation, then come back and click issue.
          </p>
        )}
        {status.pointed && !status.wwwPointed && (
          <p className="text-xs text-gray-500 mt-3">
            www doesn't point here yet — the certificate will cover {status.domain} only. Point www and re-run to include it.
          </p>
        )}
        <button onClick={load} className="text-xs text-gray-500 hover:text-gray-800 mt-3 underline">Re-check DNS</button>
      </div>
    </div>
  );
};

const DnsCheckRow: React.FC<{ name: string; ok: boolean; resolved: string[]; expected: string | null; optional?: boolean }> =
  ({ name, ok, resolved, expected, optional }) => (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <span className="font-mono text-gray-800">{name}</span>
      <span className={`text-xs ${ok ? 'text-green-700' : optional ? 'text-gray-400' : 'text-amber-700'}`}>
        {ok
          ? `✓ points to ${expected}`
          : resolved.length > 0
          ? `✗ resolves to ${resolved.join(', ')} (needs ${expected})`
          : `✗ no A record found${optional ? ' (optional)' : ''}`}
      </span>
    </div>
  );

const Row: React.FC<{
  label: string; value: string | null;
  mono?: boolean; onCopy?: () => void; copied?: boolean;
}> = ({ label, value, mono, onCopy, copied }) => (
  <div className="flex justify-between items-center text-sm py-1.5">
    <span className="text-gray-600">{label}</span>
    <div className="flex items-center gap-2">
      <span className={mono ? 'font-mono' : ''}>{value || '—'}</span>
      {onCopy && value && (
        <button onClick={onCopy} className="text-blue-600 text-xs hover:underline">
          {copied ? '✓' : 'Copy'}
        </button>
      )}
    </div>
  </div>
);

const UsageBar: React.FC<{ label: string; used: string; total: string; pct: number }> = ({ label, used, total, pct }) => {
  const cappedPct = Math.min(100, Math.max(0, pct));
  const color = cappedPct > 90 ? 'bg-red-500' : cappedPct > 75 ? 'bg-amber-500' : 'bg-blue-500';
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{label}</span>
        <span>{used} / {total}</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded">
        <div className={`h-2 rounded ${color}`} style={{ width: `${cappedPct}%` }} />
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    creating: 'bg-blue-100 text-blue-800',
    suspended: 'bg-amber-100 text-amber-800',
    terminated: 'bg-gray-200 text-gray-700',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// ============================================================================
// MySQL databases panel (Phase 2 #1)
// ============================================================================

interface MysqlDb { id: string; dbName: string; dbUser: string; dbPassword: string | null; createdAt: string; }

const MySQLDatabasesPanel: React.FC<{ hostingAccountId: string }> = ({ hostingAccountId }) => {
  const [dbs, setDbs] = useState<MysqlDb[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', username: '' });
  const [busy, setBusy] = useState(false);
  const [revealId, setRevealId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/databases`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setDbs(json.data || []);
      else setErr(json.error || 'Failed to load databases');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const create = async () => {
    if (!form.name.trim()) { setErr('Database name is required'); return; }
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/databases`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim().toLowerCase(),
          username: (form.username || form.name).trim().toLowerCase(),
        }),
      });
      const json = await resp.json();
      if (json.success) {
        setShowAdd(false);
        setForm({ name: '', username: '' });
        await load();
        setRevealId(json.data?.id || null);
      } else {
        setErr(json.error || 'Failed to create database');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d: MysqlDb) => {
    if (!window.confirm(`Drop database "${d.dbName}" and its user "${d.dbUser}"? This cannot be undone.`)) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/databases/${d.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Failed to drop database');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (d: MysqlDb) => {
    if (!window.confirm(`Rotate password for "${d.dbUser}"? Existing apps using this DB will need their config updated with the new password.`)) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/databases/${d.id}/rotate-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) {
        await load();
        setRevealId(d.id);
      } else {
        setErr(json.error || 'Failed to rotate password');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">MySQL / MariaDB Databases</h2>
          <p className="text-xs text-gray-500 mt-0.5">Connect from your code via <span className="font-mono">localhost</span> on port 3306.</p>
        </div>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
        >
          {showAdd ? 'Cancel' : 'Create Database'}
        </button>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {showAdd && (
        <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200 grid grid-cols-1 md:grid-cols-12 gap-2">
          <input
            className="md:col-span-5 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
            placeholder="db_name (lowercase, 1-32 chars)"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
          <input
            className="md:col-span-5 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
            placeholder="db_user (defaults to db_name)"
            value={form.username}
            onChange={e => setForm({ ...form, username: e.target.value })}
          />
          <button
            onClick={create}
            disabled={busy}
            className="md:col-span-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? '…' : 'Create'}
          </button>
          <p className="md:col-span-12 text-xs text-gray-500">A strong password is auto-generated and shown once after creation. You can rotate it later.</p>
        </div>
      )}

      {loading ? (
        <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
      ) : dbs.length === 0 ? (
        <div className="text-sm text-gray-500 py-2">No databases yet. Click "Create Database" to add one.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1.5 pr-3 font-medium">Database</th>
                <th className="py-1.5 pr-3 font-medium">User</th>
                <th className="py-1.5 pr-3 font-medium">Password</th>
                <th className="py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {dbs.map(d => (
                <tr key={d.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{d.dbName}</td>
                  <td className="py-2 pr-3 font-mono">{d.dbUser}</td>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {revealId === d.id && d.dbPassword ? (
                      <span className="bg-yellow-50 border border-yellow-200 rounded px-1.5 py-0.5">{d.dbPassword}</span>
                    ) : (
                      <button
                        onClick={() => setRevealId(d.id)}
                        className="text-blue-600 hover:underline"
                      >
                        Show
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => rotate(d)} disabled={busy} className="text-blue-600 text-xs hover:underline mr-3">Rotate</button>
                    <button onClick={() => remove(d)} disabled={busy} className="text-red-600 text-xs hover:underline">Drop</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Installed apps panel — Phase 2 #2 (WordPress one-click installer)
// ============================================================================

interface InstalledApp {
  id: string; appType: string; installPath: string;
  targetDomain: string | null;
  adminUser: string | null; adminEmail: string | null;
  adminPassword: string | null;
  siteTitle: string | null;
  status: 'installing' | 'installed' | 'failed' | 'removed';
  lastError: string | null;
  installedAt: string | null; createdAt: string;
}

const InstalledAppsPanel: React.FC<{ hostingAccountId: string; primaryDomain: string | null }> = ({ hostingAccountId, primaryDomain }) => {
  const [apps, setApps] = useState<InstalledApp[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showInstall, setShowInstall] = useState(false);
  const [form, setForm] = useState({
    targetDomain: primaryDomain || '',
    subfolder: '',
    adminUser: 'admin',
    adminEmail: '',
    siteTitle: 'My Site',
  });
  const [busy, setBusy] = useState(false);
  const [revealId, setRevealId] = useState<string | null>(null);
  const [justInstalled, setJustInstalled] = useState<any>(null);

  useEffect(() => {
    setForm(f => ({ ...f, targetDomain: f.targetDomain || primaryDomain || '' }));
  }, [primaryDomain]);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/apps`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setApps(json.data || []);
      else setErr(json.error || 'Failed to load apps');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const install = async () => {
    setBusy(true);
    setErr('');
    setJustInstalled(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/apps/wordpress`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetDomain: form.targetDomain.trim(),
          subfolder: form.subfolder.trim() || null,
          adminUser: form.adminUser.trim(),
          adminEmail: form.adminEmail.trim(),
          siteTitle: form.siteTitle.trim(),
        }),
      });
      const json = await resp.json();
      if (json.success) {
        setShowInstall(false);
        setJustInstalled(json.data);
        setForm({ ...form, subfolder: '', adminEmail: '' });
        await load();
      } else {
        setErr(json.error || 'Install failed');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const uninstall = async (app: InstalledApp) => {
    if (!window.confirm(`Uninstall WordPress at ${app.installPath}? This deletes the files AND the database — cannot be undone.`)) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/apps/${app.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) {
        if (justInstalled?.id === app.id) setJustInstalled(null);
        await load();
      } else {
        setErr(json.error || 'Uninstall failed');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">Installed Apps</h2>
          <p className="text-xs text-gray-500 mt-0.5">One-click installers for popular CMS / apps</p>
        </div>
        <button
          onClick={() => setShowInstall(s => !s)}
          className="text-sm bg-purple-600 text-white px-3 py-1.5 rounded hover:bg-purple-700"
        >
          {showInstall ? 'Cancel' : 'Install WordPress'}
        </button>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {justInstalled && (
        <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded">
          <div className="font-medium text-green-900 mb-2">✅ WordPress installed!</div>
          <div className="text-sm text-green-900 space-y-1">
            <div><strong>Site:</strong> <a href={justInstalled.siteUrl} target="_blank" rel="noreferrer" className="underline">{justInstalled.siteUrl}</a></div>
            <div><strong>Admin:</strong> <a href={justInstalled.adminUrl} target="_blank" rel="noreferrer" className="underline">{justInstalled.adminUrl}</a></div>
            <div><strong>Admin user:</strong> <span className="font-mono">{justInstalled.adminUser}</span></div>
            <div><strong>Admin password:</strong> <span className="font-mono bg-yellow-50 border border-yellow-200 rounded px-1.5 py-0.5">{justInstalled.adminPassword}</span></div>
            <div className="text-xs text-green-800 mt-2">Save this password now — you can reveal it later from this panel, but please write it down.</div>
          </div>
        </div>
      )}

      {showInstall && (
        <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-600">Target domain</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                placeholder={primaryDomain || 'example.com'}
                value={form.targetDomain}
                onChange={e => setForm({ ...form, targetDomain: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Subfolder (optional, e.g. "blog")</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                placeholder="leave empty to install at the root"
                value={form.subfolder}
                onChange={e => setForm({ ...form, subfolder: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Site title</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={form.siteTitle}
                onChange={e => setForm({ ...form, siteTitle: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Admin email</label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                placeholder="you@example.com"
                value={form.adminEmail}
                onChange={e => setForm({ ...form, adminEmail: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Admin user</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                value={form.adminUser}
                onChange={e => setForm({ ...form, adminUser: e.target.value })}
              />
            </div>
          </div>
          <button
            onClick={install}
            disabled={busy}
            className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
          >
            {busy ? 'Installing… (30-60s)' : 'Install WordPress'}
          </button>
          <p className="text-xs text-gray-500">A fresh DB will be created and a strong admin password generated. Save it when shown.</p>
        </div>
      )}

      {loading ? (
        <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
      ) : apps.length === 0 ? (
        <div className="text-sm text-gray-500 py-2">No apps installed yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1.5 pr-3 font-medium">App</th>
                <th className="py-1.5 pr-3 font-medium">URL</th>
                <th className="py-1.5 pr-3 font-medium">Admin</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {apps.map(a => {
                const isSubfolder = a.installPath && a.installPath !== '/';
                const siteUrl = a.targetDomain
                  ? `https://${a.targetDomain}${isSubfolder ? a.installPath : ''}`
                  : null;
                return (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      <span className="font-medium">{a.appType}</span>
                      <span className="text-gray-500 text-xs ml-1 font-mono">{a.installPath}</span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {siteUrl ? <a href={siteUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{siteUrl}</a> : '—'}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {a.adminUser ? (
                        <>
                          <span>{a.adminUser}</span>
                          <span className="mx-1">/</span>
                          {revealId === a.id && a.adminPassword ? (
                            <span className="bg-yellow-50 border border-yellow-200 rounded px-1.5 py-0.5">{a.adminPassword}</span>
                          ) : (
                            <button onClick={() => setRevealId(a.id)} className="text-blue-600 hover:underline">Show pw</button>
                          )}
                        </>
                      ) : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      <StatusPill status={a.status} />
                      {a.status === 'failed' && a.lastError && (
                        <div className="text-xs text-red-700 mt-1 font-mono break-words max-w-xs">{a.lastError.slice(0, 200)}</div>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => uninstall(a)} disabled={busy} className="text-red-600 text-xs hover:underline">Uninstall</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    installing: 'bg-blue-100 text-blue-800',
    installed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    removed: 'bg-gray-200 text-gray-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

// ============================================================================
// Backups panel (Phase 2 #4)
// ============================================================================

interface BackupRow {
  id: string;
  status: 'pending' | 'creating' | 'uploading' | 'completed' | 'failed' | 'restoring' | 'deleting' | 'deleted' | 'expired';
  progressPercent: number;
  fileSizeBytes: number | null;
  fileName: string | null;
  startedAt: string | null;
  completedAt: string | null;
  retentionDays: number;
  expiresAt: string | null;
  isProtected: boolean;
  triggeredBy: string;
  errorMessage: string | null;
  createdAt: string;
}

const BackupsPanel: React.FC<{ hostingAccountId: string }> = ({ hostingAccountId }) => {
  const [backups, setBackups] = useState<BackupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/backups`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setBackups(json.data || []);
      else setErr(json.error || 'Failed to load backups');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  // Poll every 5s while any backup is in a non-terminal state.
  useEffect(() => {
    const inProgress = backups.some(b => ['pending', 'creating', 'uploading', 'restoring', 'deleting'].includes(b.status));
    if (inProgress && !pollRef.current) {
      pollRef.current = setInterval(load, 5000);
    } else if (!inProgress && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [backups.map(b => b.status).join(',')]);

  const createNow = async () => {
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/backups`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Failed to start backup');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const restore = async (b: BackupRow) => {
    if (!window.confirm(
      `Restore from this backup?\n\nThis will OVERWRITE your hosting account with the contents of the snapshot. Anything you've changed since the backup was taken will be lost. The site will be briefly unavailable during restore.`
    )) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/backups/${b.id}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) {
        alert('Restore complete. Reload your site to verify.');
        await load();
      } else {
        setErr(json.error || 'Restore failed');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b: BackupRow) => {
    if (!window.confirm('Delete this backup? This removes it from S3 and cannot be undone.')) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/backups/${b.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Delete failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const formatSize = (b: number | null): string => {
    if (!b) return '—';
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">Backups</h2>
          <p className="text-xs text-gray-500 mt-0.5">Daily automatic snapshots stored in S3. Restore any time.</p>
        </div>
        <button
          onClick={createNow}
          disabled={busy}
          className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {busy ? '…' : 'Back Up Now'}
        </button>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {loading ? (
        <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
      ) : backups.length === 0 ? (
        <div className="text-sm text-gray-500 py-2">No backups yet. Click "Back Up Now" or wait for the nightly automatic backup.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1.5 pr-3 font-medium">When</th>
                <th className="py-1.5 pr-3 font-medium">Size</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 pr-3 font-medium">Trigger</th>
                <th className="py-1.5 pr-3 font-medium">Expires</th>
                <th className="py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {backups.map(b => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-xs">{new Date(b.createdAt).toLocaleString()}</td>
                  <td className="py-2 pr-3">{formatSize(b.fileSizeBytes)}</td>
                  <td className="py-2 pr-3">
                    <BackupStatusPill status={b.status} />
                    {b.status === 'failed' && b.errorMessage && (
                      <div className="text-xs text-red-700 mt-1 font-mono break-words max-w-xs">{b.errorMessage.slice(0, 200)}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs capitalize">{b.triggeredBy}</td>
                  <td className="py-2 pr-3 text-xs">
                    {b.expiresAt ? new Date(b.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 text-right">
                    {b.status === 'completed' && (
                      <>
                        <button onClick={() => restore(b)} disabled={busy} className="text-blue-600 text-xs hover:underline mr-3">Restore</button>
                        {!b.isProtected && (
                          <button onClick={() => remove(b)} disabled={busy} className="text-red-600 text-xs hover:underline">Delete</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Cron jobs panel (Phase 2 #6)
// ============================================================================

interface CronRow {
  id: string;
  schedule: string;
  command: string;
  label: string | null;
  enabled: boolean;
  last_run_at: string | null;
  created_at: string;
}

const CRON_PRESETS: { label: string; value: string }[] = [
  { label: 'Every minute', value: '* * * * *' },
  { label: 'Every 5 minutes', value: '*/5 * * * *' },
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Hourly', value: '0 * * * *' },
  { label: 'Daily at 03:00', value: '0 3 * * *' },
  { label: 'Weekly (Sun 03:00)', value: '0 3 * * 0' },
  { label: 'Monthly (day 1, 03:00)', value: '0 3 1 * *' },
];

const CronJobsPanel: React.FC<{ hostingAccountId: string }> = ({ hostingAccountId }) => {
  const [jobs, setJobs] = useState<CronRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ schedule: '0 3 * * *', command: '', label: '' });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/cron`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setJobs(json.data || []);
      else setErr(json.error || 'Failed to load cron jobs');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const startEdit = (j: CronRow) => {
    setEditingId(j.id);
    setForm({ schedule: j.schedule, command: j.command, label: j.label || '' });
    setShowAdd(true);
  };

  const cancelForm = () => {
    setShowAdd(false);
    setEditingId(null);
    setForm({ schedule: '0 3 * * *', command: '', label: '' });
  };

  const submit = async () => {
    if (!form.command.trim()) { setErr('Command is required'); return; }
    if (!form.schedule.trim()) { setErr('Schedule is required'); return; }
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${apiBase}/api/hosting/accounts/${hostingAccountId}/cron/${editingId}`
        : `${apiBase}/api/hosting/accounts/${hostingAccountId}/cron`;
      const resp = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schedule: form.schedule.trim(),
          command: form.command.trim(),
          label: form.label.trim() || null,
        }),
      });
      const json = await resp.json();
      if (json.success) {
        cancelForm();
        await load();
      } else {
        setErr(json.error || 'Save failed');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (j: CronRow) => {
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/cron/${j.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !j.enabled }),
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Toggle failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (j: CronRow) => {
    if (!window.confirm(`Delete this cron job?\n\n${j.schedule} ${j.command}`)) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/cron/${j.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Delete failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">Cron Jobs</h2>
          <p className="text-xs text-gray-500 mt-0.5">Schedule recurring commands. Runs as your SFTP user inside your container.</p>
        </div>
        <button
          onClick={() => (showAdd ? cancelForm() : setShowAdd(true))}
          className="text-sm bg-orange-600 text-white px-3 py-1.5 rounded hover:bg-orange-700"
        >
          {showAdd ? 'Cancel' : 'Add Cron Job'}
        </button>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {showAdd && (
        <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-3">
              <label className="text-xs text-gray-600">Schedule</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                value={form.schedule}
                onChange={e => setForm({ ...form, schedule: e.target.value })}
                placeholder="0 3 * * *"
              />
              <select
                className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-xs"
                value=""
                onChange={e => e.target.value && setForm({ ...form, schedule: e.target.value })}
              >
                <option value="">Preset…</option>
                {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label} ({p.value})</option>)}
              </select>
            </div>
            <div className="md:col-span-7">
              <label className="text-xs text-gray-600">Command</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                value={form.command}
                onChange={e => setForm({ ...form, command: e.target.value })}
                placeholder="/usr/bin/php /home/youruser/public_html/cron.php"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-gray-600">Label (optional)</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="WP cron"
              />
            </div>
          </div>
          <button
            onClick={submit}
            disabled={busy}
            className="bg-orange-600 text-white px-4 py-2 rounded text-sm hover:bg-orange-700 disabled:opacity-50"
          >
            {busy ? '…' : (editingId ? 'Save Changes' : 'Add')}
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
      ) : jobs.length === 0 ? (
        <div className="text-sm text-gray-500 py-2">No cron jobs yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1.5 pr-3 font-medium">Schedule</th>
                <th className="py-1.5 pr-3 font-medium">Command</th>
                <th className="py-1.5 pr-3 font-medium">Label</th>
                <th className="py-1.5 pr-3 font-medium">Enabled</th>
                <th className="py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{j.schedule}</td>
                  <td className="py-2 pr-3 font-mono text-xs break-words max-w-md">{j.command}</td>
                  <td className="py-2 pr-3 text-xs">{j.label || '—'}</td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => toggle(j)}
                      disabled={busy}
                      className={`text-xs px-2 py-0.5 rounded ${j.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}
                    >
                      {j.enabled ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => startEdit(j)} disabled={busy} className="text-blue-600 text-xs hover:underline mr-3">Edit</button>
                    <button onClick={() => remove(j)} disabled={busy} className="text-red-600 text-xs hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Subdomains panel (Phase 2 #5)
// ============================================================================

interface Subdomain {
  id: string;
  subdomain_label: string;
  full_fqdn: string;
  docroot: string;
  ssl_issued_at: string | null;
  status: string;
  created_at: string;
}

const SubdomainsPanel: React.FC<{ hostingAccountId: string; primaryDomain: string | null }> = ({ hostingAccountId, primaryDomain }) => {
  const [subs, setSubs] = useState<Subdomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/subdomains`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setSubs(json.data || []);
      else setErr(json.error || 'Failed to load subdomains');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hostingAccountId]);

  const create = async () => {
    const trimmed = label.trim().toLowerCase();
    if (!trimmed) { setErr('Subdomain label is required'); return; }
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/subdomains`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: trimmed }),
      });
      const json = await resp.json();
      if (json.success) {
        setShowAdd(false);
        setLabel('');
        await load();
      } else {
        setErr(json.error || 'Create failed');
      }
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s: Subdomain) => {
    if (!window.confirm(`Delete subdomain ${s.full_fqdn}? Files in ${s.docroot} and the DNS record will be removed.`)) return;
    setBusy(true);
    setErr('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const resp = await fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/subdomains/${s.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Delete failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  if (!primaryDomain) {
    return null;
  }

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">Subdomains</h2>
          <p className="text-xs text-gray-500 mt-0.5">Create sub.{primaryDomain} aliases for blogs, staging, etc.</p>
        </div>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="text-sm bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700"
        >
          {showAdd ? 'Cancel' : 'Add Subdomain'}
        </button>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {showAdd && (
        <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200 flex gap-2 items-center">
          <input
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
            placeholder="blog"
            value={label}
            onChange={e => setLabel(e.target.value)}
          />
          <span className="text-gray-500 font-mono text-sm">.{primaryDomain}</span>
          <button
            onClick={create}
            disabled={busy}
            className="bg-teal-600 text-white px-4 py-1.5 rounded text-sm hover:bg-teal-700 disabled:opacity-50"
          >
            {busy ? '…' : 'Create'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mx-auto" /></div>
      ) : subs.length === 0 ? (
        <div className="text-sm text-gray-500 py-2">No subdomains yet. Add one to point sub.{primaryDomain} at this account.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1.5 pr-3 font-medium">Subdomain</th>
                <th className="py-1.5 pr-3 font-medium">Docroot</th>
                <th className="py-1.5 pr-3 font-medium">SSL</th>
                <th className="py-1.5 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => {
                const httpScheme = s.ssl_issued_at ? 'https' : 'http';
                return (
                  <tr key={s.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono">
                      <a href={`${httpScheme}://${s.full_fqdn}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{s.full_fqdn}</a>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{s.docroot}</td>
                    <td className="py-2 pr-3">
                      {s.ssl_issued_at ? <span className="text-green-700 text-xs">✓ Issued</span> : <span className="text-gray-500 text-xs">Pending</span>}
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => remove(s)} disabled={busy} className="text-red-600 text-xs hover:underline">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BackupStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    creating: 'bg-blue-100 text-blue-800',
    uploading: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    restoring: 'bg-amber-100 text-amber-800',
    deleting: 'bg-gray-200 text-gray-700',
    expired: 'bg-gray-200 text-gray-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

export default HostingAccountDetail;
